<script setup lang="ts">
const localePath = useLocalePath()
const { t } = useI18n()

useHead({
  titleTemplate: () => t("botsConstructor.title") || t("seo.title"),
  meta: [
    { name: "description", content: () => `${t("botsConstructor.subTitleElements[0]")} ${t("botsConstructor.subTitleElements[1]")} ${t("botsConstructor.subTitleElements[2]")}` },
    { property: "og:title", content: () => t("botsConstructor.title") || t("seo.title") },
    { property: "og:description", content: () => `${t("botsConstructor.subTitleElements[0]")} ${t("botsConstructor.subTitleElements[1]")} ${t("botsConstructor.subTitleElements[2]")}` },
    { property: "og:image", content: "https://smsbat.com/images/bots-constructor.png" },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary_large_image" }
  ]
})

const scrollToPricing = () => {
  const element = document.getElementById('pricing');
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
}

onMounted(() => {
  const el = document.querySelector("body")
  el?.scrollIntoView()
  if (globalThis.location.hash) {
    const el = document.querySelector(globalThis.location.hash)
    if (el) {
      el.scrollIntoView()
    }
  }
})
</script>

<template>
  <div class="bots-constructor-page">
    <!-- Hero Section -->
    <div class="main-banner">
      <div class="container">
        <div class="banner-content">
          <div class="banner-text">
            <h1 class="banner-title">
              {{ $t('botsConstructor.title') }}
            </h1>
            <p class="banner-description">
              {{ $t('botsConstructor.subtitle') }}
            </p>
            <div class="banner-action">
              <NuxtLink :to="localePath('/contacts')" class="nav-link button filled">
                <p>{{ $t('botsConstructor.hero.getStarted') }}</p>
              </NuxtLink>
              <button @click="scrollToPricing" class="button outlined">
                <p>{{ $t('botsConstructor.hero.viewPricing') }}</p>
              </button>
            </div>
          </div>
          <div class="banner-image-container">
            <NuxtImg src="/images/bots-constructor.png" class="mt-20 w-full h-auto object-contain" alt="Bots Constructor Illustration" width="1080" height="810" format="webp" loading="eager" fetchpriority="high" />
          </div>
        </div>
      </div>
    </div>

    <!-- Global Stats Section -->
    <div class="page-section bg-gray-50">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('botsConstructor.stats.title') }}</div>
          <div class="section-head-description">{{ $t('botsConstructor.stats.subtitle') }}</div>
        </div>
        <div class="stats-grid">
          <div class="stat-item">
            <div class="stat-number">3</div>
            <div class="stat-label">{{ $t('botsConstructor.stats.platforms') }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">24/7</div>
            <div class="stat-label">{{ $t('botsConstructor.stats.support') }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">99.9%</div>
            <div class="stat-label">{{ $t('botsConstructor.stats.uptime') }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-number">∞</div>
            <div class="stat-label">{{ $t('botsConstructor.stats.scalability') }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Features Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('botsConstructor.features.title') }}</div>
          <div class="section-head-description">{{ $t('botsConstructor.features.subtitle') }}</div>
        </div>
        <div class="features-grid">
          <div class="feature-item">
            <div class="feature-icon">
              <svg class="mx-auto" width="48" height="48" viewBox="0 0 24 24" fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="#3070b6" stroke-width="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5" fill="#3070b6"></circle>
                <path d="M21 15L16 10L5 21" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
              </svg>
            </div>
            <h3>{{ $t('botsConstructor.features.dragDrop.title') }}</h3>
            <p>{{ $t('botsConstructor.features.dragDrop.description') }}</p>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <svg class="mx-auto" width="48" height="48" viewBox="0 0 24 24" fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
                <path d="M2 17L12 22L22 17" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
                <path d="M2 12L12 17L22 12" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
              </svg>
            </div>
            <h3>{{ $t('botsConstructor.features.multiPlatform.title') }}</h3>
            <p>{{ $t('botsConstructor.features.multiPlatform.description') }}</p>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <svg class="mx-auto" width="48" height="48" viewBox="0 0 24 24" fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12L11 14L15 10" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
                <path
                  d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                  stroke="#3070b6" stroke-width="2"></path>
              </svg>
            </div>
            <h3>{{ $t('botsConstructor.features.readyTemplates.title') }}</h3>
            <p>{{ $t('botsConstructor.features.readyTemplates.description') }}</p>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <svg class="mx-auto" width="48" height="48" viewBox="0 0 24 24" fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
              </svg>
            </div>
            <h3>{{ $t('botsConstructor.features.fastDeployment.title') }}</h3>
            <p>{{ $t('botsConstructor.features.fastDeployment.description') }}</p>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <svg class="mx-auto" width="48" height="48" viewBox="0 0 24 24" fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22S2 16 2 10V4L12 2L22 4V10C22 16 12 22 12 22Z" stroke="#3070b6" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round"></path>
                <path d="M9 12L11 14L15 10" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
              </svg>
            </div>
            <h3>{{ $t('botsConstructor.features.secure.title') }}</h3>
            <p>{{ $t('botsConstructor.features.secure.description') }}</p>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <svg class="mx-auto" width="48" height="48" viewBox="0 0 24 24" fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path d="M3 3H21V21H3V3Z" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
                <path d="M9 9H15V15H9V9Z" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
              </svg>
            </div>
            <h3>{{ $t('botsConstructor.features.analytics.title') }}</h3>
            <p>{{ $t('botsConstructor.features.analytics.description') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Platforms Section -->
    <div class="page-section bg-gray-50">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('botsConstructor.platforms.title') }}</div>
          <div class="section-head-description">{{ $t('botsConstructor.platforms.subtitle') }}</div>
        </div>
        <div class="platforms-grid">
          <div class="platform-item">
            <div class="platform-icon">
              <NuxtImg class="mx-auto rounded-[20px]" src="/images/icons/viber-bot-icon.svg" width="60" height="60" alt="Viber Bot" format="webp" loading="lazy" />
            </div>
            <h3>{{ $t('botsConstructor.platforms.viber.title') }}</h3>
            <p>{{ $t('botsConstructor.platforms.viber.description') }}</p>
          </div>
          <div class="platform-item">
            <div class="platform-icon">
              <NuxtImg class="mx-auto rounded-[20px]" src="/images/icons/facebook-icon-blue.svg" width="60" height="60" alt="Meta" format="webp" loading="lazy" />
            </div>
            <h3>{{ $t('botsConstructor.platforms.meta.title') }}</h3>
            <p>{{ $t('botsConstructor.platforms.meta.description') }}</p>
          </div>
          <div class="platform-item">
            <div class="platform-icon">
              <NuxtImg class="mx-auto rounded-[20px]" src="/images/icons/telegram-icon.svg" width="60" height="60" alt="Telegram" format="webp" loading="lazy" />
            </div>
            <h3>{{ $t('botsConstructor.platforms.telegram.title') }}</h3>
            <p>{{ $t('botsConstructor.platforms.telegram.description') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- How It Works Section -->
    <div class="page-section">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('botsConstructor.howItWorks.title') }}</div>
        </div>
        <div class="steps-grid">
          <div class="step-item">
            <div class="step-number">1</div>
            <h3>{{ $t('botsConstructor.howItWorks.step1.title') }}</h3>
            <p>{{ $t('botsConstructor.howItWorks.step1.description') }}</p>
          </div>
          <div class="step-item">
            <div class="step-number">2</div>
            <h3>{{ $t('botsConstructor.howItWorks.step2.title') }}</h3>
            <p>{{ $t('botsConstructor.howItWorks.step2.description') }}</p>
          </div>
          <div class="step-item">
            <div class="step-number">3</div>
            <h3>{{ $t('botsConstructor.howItWorks.step3.title') }}</h3>
            <p>{{ $t('botsConstructor.howItWorks.step3.description') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Interface Showcase Section -->
    <div class="page-section showcase-section overflow-hidden object-contain my-32 py-10">
      <div class="container">
        <div class="section-head text-center">
          <div class="section-head-title text-4xl font-bold mb-4">{{ $t('botsConstructor.title') }} Showcase</div>
          <div class="section-head-description text-xl text-gray-400">Visual Drag-and-Drop Editor</div>
        </div>
        <div class="showcase-wrapper relative max-w-6xl mx-auto rounded-3xl overflow-hidden shadow-2xl transition-transform hover:scale-[1.02] duration-500 bg-white" style="border: 2px solid rgba(255, 255, 255, 0.2);">
            <div class="window-bar flex items-center px-4 py-3 bg-gray-100 border-b border-gray-200">
              <div class="flex gap-2">
                <div class="w-3 h-3 rounded-full bg-red-400"></div>
                <div class="w-3 h-3 rounded-full bg-yellow-400"></div>
                <div class="w-3 h-3 rounded-full bg-green-400"></div>
              </div>
              <div class="mx-auto text-sm text-gray-500 font-medium font-mono">omni.smsbat.com</div>
            </div>
            <NuxtImg src="/images/bot-builder-showcase.png" alt="Drag and Drop Flow Constructor Interface" class="w-full h-auto object-contain" width="2940" height="1604" format="webp" loading="lazy" />
        </div>
      </div>
    </div>

    <!-- Pricing Section -->
    <div class="page-section bg-gray-50" id="pricing">
      <div class="container">
        <div class="section-head">
          <div class="section-head-title">{{ $t('botsConstructor.pricingPlans.title') }}</div>
          <div class="section-head-description">{{ $t('botsConstructor.pricingPlans.description') }}</div>
        </div>
        <div class="pricing-grid">
          <div class="pricing-card">
            <div class="pricing-header">
              <h3>{{ $t('botsConstructor.pricingPlans.plans[0].name') }}</h3>
              <div class="pricing-price">
                <span class="currency">{{ $t('botsConstructor.pricingPlans.plans[0].price.currency') }}</span>
                <span class="amount">{{ $t('botsConstructor.pricingPlans.plans[0].price.amount') }}</span>
                <span class="period">{{ $t('botsConstructor.pricingPlans.plans[0].price.period') }}</span>
              </div>
            </div>
            <div class="pricing-features">
              <div class="feature-item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10" stroke="#10B981" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round"></path>
                  <path
                    d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="#10B981" stroke-width="2"></path>
                </svg>
                <span>{{ $t('botsConstructor.pricingPlans.plans[0].features[0]') }}</span>
              </div>
              <div class="feature-item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10" stroke="#10B981" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round"></path>
                  <path
                    d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="#10B981" stroke-width="2"></path>
                </svg>
                <span>{{ $t('botsConstructor.pricingPlans.plans[0].features[1]') }}</span>
              </div>
              <div class="feature-item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10" stroke="#10B981" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round"></path>
                  <path
                    d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="#10B981" stroke-width="2"></path>
                </svg>
                <span>{{ $t('botsConstructor.pricingPlans.plans[0].features[2]') }}</span>
              </div>
            </div>
            <div class="pricing-action">
              <NuxtLink :to="localePath('/contacts')" class="button outlined">
                {{ $t('botsConstructor.pricingPlans.plans[0].button') }}
              </NuxtLink>
            </div>
          </div>

          <div class="pricing-card featured">
            <div class="pricing-badge">{{ $t('global.mostPopular') }}</div>
            <div class="pricing-header">
              <h3>{{ $t('botsConstructor.pricingPlans.plans[1].name') }}</h3>
              <div class="pricing-price">
                <span class="currency">{{ $t('botsConstructor.pricingPlans.plans[1].price.currency') }}</span>
                <span class="amount">{{ $t('botsConstructor.pricingPlans.plans[1].price.amount') }}</span>
                <span class="period">{{ $t('botsConstructor.pricingPlans.plans[1].price.period') }}</span>
              </div>
            </div>
            <div class="pricing-features">
              <div class="feature-item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10" stroke="#10B981" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round"></path>
                  <path
                    d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="#10B981" stroke-width="2"></path>
                </svg>
                <span>{{ $t('botsConstructor.pricingPlans.plans[1].features[0]') }}</span>
              </div>
              <div class="feature-item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10" stroke="#10B981" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round"></path>
                  <path
                    d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="#10B981" stroke-width="2"></path>
                </svg>
                <span>{{ $t('botsConstructor.pricingPlans.plans[1].features[1]') }}</span>
              </div>
              <div class="feature-item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10" stroke="#10B981" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round"></path>
                  <path
                    d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="#10B981" stroke-width="2"></path>
                </svg>
                <span>{{ $t('botsConstructor.pricingPlans.plans[1].features[2]') }}</span>
              </div>
              <div class="feature-item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10" stroke="#10B981" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round"></path>
                  <path
                    d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="#10B981" stroke-width="2"></path>
                </svg>
                <span>{{ $t('botsConstructor.pricingPlans.plans[1].features[3]') }}</span>
              </div>
            </div>
            <div class="pricing-action">
              <NuxtLink :to="localePath('/contacts')" class="button filled">
                {{ $t('botsConstructor.pricingPlans.plans[1].button') }}
              </NuxtLink>
            </div>
          </div>

          <div class="pricing-card">
            <div class="pricing-header">
              <h3>{{ $t('botsConstructor.pricingPlans.plans[2].name') }}</h3>
              <div class="pricing-price">
                <span class="currency">{{ $t('botsConstructor.pricingPlans.plans[2].price.currency') }}</span>
                <span class="amount">{{ $t('botsConstructor.pricingPlans.plans[2].price.amount') }}</span>
                <span class="period">{{ $t('botsConstructor.pricingPlans.plans[2].price.period') }}</span>
              </div>
            </div>
            <div class="pricing-features">
              <div class="feature-item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10" stroke="#10B981" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round"></path>
                  <path
                    d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="#10B981" stroke-width="2"></path>
                </svg>
                <span>{{ $t('botsConstructor.pricingPlans.plans[2].features[0]') }}</span>
              </div>
              <div class="feature-item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10" stroke="#10B981" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round"></path>
                  <path
                    d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="#10B981" stroke-width="2"></path>
                </svg>
                <span>{{ $t('botsConstructor.pricingPlans.plans[2].features[1]') }}</span>
              </div>
              <div class="feature-item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10" stroke="#10B981" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round"></path>
                  <path
                    d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="#10B981" stroke-width="2"></path>
                </svg>
                <span>{{ $t('botsConstructor.pricingPlans.plans[2].features[2]') }}</span>
              </div>
              <div class="feature-item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12L11 14L15 10" stroke="#10B981" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round"></path>
                  <path
                    d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="#10B981" stroke-width="2"></path>
                </svg>
                <span>{{ $t('botsConstructor.pricingPlans.plans[2].features[3]') }}</span>
              </div>
            </div>
            <div class="pricing-action">
              <NuxtLink :to="localePath('/contacts')" class="button outlined" style="white-space: normal;">
                {{ $t('botsConstructor.pricingPlans.plans[2].button') }}
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- CTA Section -->
    <div class="page-section bg-gradient bottom-banner">
      <div class="container">
        <div class="section-content text-center">
          <div class="cta-content">
            <h2 class="section-head-title mb-4">Start Building Your Bot Today!</h2>
            <p class="text-gray-600 mb-8">Join thousands of businesses using our constructor to create powerful chat
              bots. No coding required!</p>
            <div class="banner-action flex flex-wrap items-center justify-center gap-4">
              <NuxtLink :to="localePath('/contacts')" class="nav-link button filled">
                <p>Get Started</p>
              </NuxtLink>
              <NuxtLink :to="localePath('/register-bot')" class="button outlined">
                <p>Register Bot</p>
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.bots-constructor-page {
  padding-top: 100px;
}

.main-banner {
  padding: 120px 0 80px 0;
  min-height: 80vh;
  display: flex;
  align-items: center;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.banner-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

.banner-text {
  flex: 1;
}

.banner-title {
  font-size: 3rem;
  font-weight: bold;
  margin-bottom: 20px;
  line-height: 1.2;
}

.banner-description {
  font-size: 1.2rem;
  opacity: 0.9;
  line-height: 1.6;
}

.banner-image-container {
  flex: 1;
  text-align: center;
}

.banner-action {
  display: flex;
  gap: 20px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 30px;
}

.page-section {
  padding: 80px 0;
}

.bg-gray-50 {
  background: #f8f9fa;
}

.section-head {
  text-align: center;
  margin-bottom: 60px;
}

.section-head-title {
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 20px;
  color: #333;
}

.section-head-description {
  font-size: 1.2rem;
  color: #666;
  max-width: 600px;
  margin: 0 auto;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  margin-top: 40px;
}

.stat-item {
  text-align: center;
  padding: 30px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 10px;
  }
}

.stat-number {
  font-size: 3rem;
  font-weight: bold;
  color: #3070b6;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
}

.stat-label {
  font-size: 1.1rem;
  color: #666;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  margin-top: 40px;
}

.feature-item {
  text-align: center;
  padding: 30px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
}

.feature-icon {
  margin-bottom: 20px;
}

.feature-item h3 {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 15px;
  color: #333;
}

.feature-item p {
  color: #666;
  line-height: 1.6;
}

.platforms-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  margin-top: 40px;
}

.platform-item {
  text-align: center;
  padding: 40px 30px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
}

.platform-icon {
  margin-bottom: 20px;
}

.platform-icon img {
  width: 80px;
  height: 80px;
  object-fit: contain;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
}

.platform-item h3 {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 15px;
  color: #333;
}

.platform-item p {
  color: #666;
  line-height: 1.6;
}

.steps-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  margin-top: 40px;
}

.step-item {
  text-align: center;
  padding: 30px;
}

.step-number {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #3070b6;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0 auto 20px;
}

.step-item h3 {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 15px;
  color: #333;
}

.step-item p {
  color: #666;
  line-height: 1.6;
}

.pricing-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-top: 40px;
}

.pricing-card {
  background: white;
  border-radius: 15px;
  padding: 40px 30px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  position: relative;
  text-align: center;
}

.pricing-card.featured {
  border: 2px solid #3070b6;
  transform: scale(1.05);
}

.pricing-badge {
  position: absolute;
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
  background: #3070b6;
  color: white;
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: bold;
}

.pricing-header {
  margin-bottom: 30px;
}

.pricing-header h3 {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 15px;
  color: #333;
}

.pricing-price {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 5px;
}

.currency {
  font-size: 1.5rem;
  color: #666;
}

.amount {
  font-size: 3rem;
  font-weight: bold;
  color: #3070b6;
}

.period {
  font-size: 1rem;
  color: #666;
}

.pricing-features {
  margin-bottom: 30px;
}

.pricing-features .feature-item {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
  padding: 0;
  background: none;
  box-shadow: none;
  text-align: left;
}

.pricing-action {
  margin-top: 30px;
}

.nav-link {
  text-decoration: none;
}

.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  height: 52px;
  font-size: 18px;
  font-weight: 800;
  border: 2px solid #3070b6;
  border-radius: 10px;
  transition: 0.2s ease;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
}

.button.filled {
  color: #fff;
  background: #3070b6;
}

.button.filled:hover {
  color: #3070b6;
  background: #fff;
}

.button.outlined {
  color: #3070b6;
  background: #fff;
}

.button.outlined:hover {
  color: #fff;
  background: #3070b6;
}

.bg-gradient {
  background: linear-gradient(135deg, #4f46e5 0%, #764ba2 100%);
  color: white;

  .section-head-title {
    color: white;
  }

  .text-gray-600 {
    color: #111827;
  }
}

.bottom-banner {
  padding: 80px 0;
}

.cta-content {
  max-width: 800px;
  margin: 0 auto;
}


@media (max-width: 768px) {
  .banner-content {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .banner-title {
    font-size: 2rem;
  }

  .banner-description {
    font-size: 1rem;
  }

  .banner-action {
    justify-content: center;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  .features-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .platforms-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .steps-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .pricing-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .pricing-card.featured {
    transform: none;
  }

  .section-head-title {
    font-size: 2rem;
  }
}

@media (max-width: 768px) {
  .main-banner {
    .banner-title {
      margin: 0 auto;
    }

    .banner-description {
      margin: 0 auto;
    }
  }
}

@media (max-width: 992px) {
  .main-banner {
    padding: 0 0 80px 0;
  }

  .bots-constructor-page {
    padding-top: 0;
  }
}
</style>